import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { User } from '../services/playerService';

interface UserContextType {
  currentUsername: string;
  currentUser: User | null;
  setCurrentUsername: (username: string) => void;
  setCurrentUser: (user: User | null) => void;
  isUserSwitching: boolean;
  setIsUserSwitching: (switching: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [currentUsername, setCurrentUsernameState] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isUserSwitching, setIsUserSwitching] = useState(false);
  
  // Use ref to avoid dependency issues
  const usernameRef = useRef(currentUsername);
  usernameRef.current = currentUsername;

  const setCurrentUsername = useCallback((username: string) => {
    if (username === usernameRef.current) return; // No change needed
    
    console.log(`🔄 UserContext: Switching from ${usernameRef.current} to ${username}`);
    setIsUserSwitching(true);
    
    // Update state immediately without setTimeout
    setCurrentUsernameState(username);
    setCurrentUser(null); // Clear current user data
    
    // Use requestAnimationFrame for smooth UI update
    requestAnimationFrame(() => {
      setIsUserSwitching(false);
      console.log(`✅ UserContext: User switched to ${username}`);
    });
  }, []); // No dependencies to prevent infinite loops

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    currentUser,
    setCurrentUser,
    currentUsername,
    setCurrentUsername,
    isUserSwitching,
    setIsUserSwitching,
  }), [currentUser, currentUsername, isUserSwitching, setCurrentUsername]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
